import { IDevice } from '@gnaudio/jabra-js';
import { useCallback } from 'react';

import { notificationActions } from 'components/notifications/actions/notification';
import { useNotifications } from 'hooks/useNotifications';
import {
  jabraActions,
  JabraDeviceError,
  jabraDeviceManager,
} from 'middleware/jabra';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

/**
 * Use Jabra Devices
 *
 * Sets up event listeners to the devices manager and dispatches actions used by
 * jabra and devices middleware
 *
 * @returns Initializer and de-initializer functions
 */

export const useJabraDevices = (): [
  () => Promise<void>,
  () => Promise<void>,
] => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const addNotification = useNotifications();

  const deviceAdded = useCallback(
    (device: IDevice) => {
      dispatch(
        jabraActions.jabraAttached({
          device: {
            deviceID: device.id,
            productID: device.productId,
            deviceName: device.name,
            serialNumber: device.serialNumber,
          },
        }),
      );
    },
    [dispatch],
  );

  const deviceRemoved = useCallback(
    (device: IDevice) => {
      dispatch(
        jabraActions.jabraDetached({
          device: {
            deviceID: device.id,
            productID: device.productId,
            deviceName: device.name,
            serialNumber: device.serialNumber,
          },
        }),
      );
    },
    [dispatch],
  );

  const deviceList = useCallback(
    (devices: IDevice[]) => {
      dispatch(
        jabraActions.jabraListSucceeded({
          list: devices.map((device) => ({
            deviceID: device.id,
            productID: device.productId,
            deviceName: device.name,
            serialNumber: device.serialNumber,
          })),
        }),
      );
    },
    [dispatch],
  );

  const deviceChange = useCallback(
    (currentDevice) => {
      dispatch(
        jabraActions.jabraListSucceeded({
          list: jabraDeviceManager.devices.map((device) => ({
            deviceID: device.id,
            productID: device.productId,
            deviceName: device.name,
            serialNumber: device.serialNumber,
            isLocked: device.serialNumber === currentDevice.serialNumber,
          })),
        }),
      );
    },
    [dispatch],
  );

  const callAccepted = useCallback(() => {
    dispatch(jabraActions.jabraCallAccepted());
  }, [dispatch]);

  const callRejected = useCallback(() => {
    dispatch(jabraActions.jabraCallRejected());
  }, [dispatch]);

  const hangup = useCallback(() => {
    dispatch(
      jabraActions.jabraButtonClicked({
        button: 'OffHook',
        buttonValue: false,
      }),
    );
  }, [dispatch]);

  const error = useCallback(
    (error: JabraDeviceError) => {
      const notificationType = 'jabra-error';

      addNotification({
        type: notificationType,
        title: error.message,
        text: 'Try disconnect and reconnect your Jabra device',
        icon: 'headset',
        className: 'danger',
        rightAction: {
          text: 'Configure Jabra devices',
          action: () => {
            navigate({ path: '/settings/integrations/jabra' });
            dispatch(
              notificationActions.notificationRemoveByType({
                type: notificationType,
              }),
            );
          },
        },
      });
    },
    [navigate, dispatch, addNotification],
  );

  const initializeJabraDevices = useCallback(async () => {
    const initSuccess = await jabraDeviceManager.init();
    if (initSuccess) {
      jabraDeviceManager.addEventListener('deviceAdded', deviceAdded);
      jabraDeviceManager.addEventListener('deviceRemoved', deviceRemoved);
      jabraDeviceManager.addEventListener('devicesList', deviceList);
      jabraDeviceManager.addEventListener('deviceChange', deviceChange);
      jabraDeviceManager.addEventListener('callAccepted', callAccepted);
      jabraDeviceManager.addEventListener('callRejected', callRejected);
      jabraDeviceManager.addEventListener('hangup', hangup);
      jabraDeviceManager.addEventListener('error', error);
    }
  }, [
    deviceAdded,
    deviceRemoved,
    deviceList,
    deviceChange,
    callAccepted,
    callRejected,
    hangup,
    error,
  ]);

  const removeJabraDevices = useCallback(async () => {
    jabraDeviceManager.removeEventListener('deviceAdded', deviceAdded);
    jabraDeviceManager.removeEventListener('deviceRemoved', deviceRemoved);
    jabraDeviceManager.removeEventListener('devicesList', deviceList);
    jabraDeviceManager.removeEventListener('deviceChange', deviceChange);
    jabraDeviceManager.removeEventListener('callAccepted', callAccepted);
    jabraDeviceManager.removeEventListener('callRejected', callRejected);
    jabraDeviceManager.removeEventListener('hangup', hangup);
    jabraDeviceManager.removeEventListener('error', error);
    await jabraDeviceManager.deinit();
  }, [
    deviceAdded,
    deviceRemoved,
    deviceList,
    deviceChange,
    callAccepted,
    callRejected,
    hangup,
    error,
  ]);

  return [initializeJabraDevices, removeJabraDevices];
};
